import { useNavigate } from 'react-router-dom';
import { AlertTriangle, BrainCircuit, Database, RefreshCw, Shapes, Waves } from 'lucide-react';
import { useInterpreterStatus } from './api';

function StatCard({ title, count, icon: Icon, color }: { title: string; count: number; icon: any; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
      <div className={`w-11 h-11 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{count}</div>
        <div className="text-xs text-gray-500 font-medium">{title}</div>
      </div>
    </div>
  );
}

function QuickAction({ label, icon: Icon, color, onClick }: { label: string; icon: any; color: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all hover:shadow-md active:scale-[0.98] ${color}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

export default function InterpreterDashboard() {
  const navigate = useNavigate();
  const statusQ = useInterpreterStatus();
  const status = statusQ.data;

  return (
    <div className="space-y-6 pt-4 animate-fade-in">
      {statusQ.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4 h-20 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="UNSPSC Mappings" count={status?.catalogs?.unspsc_count ?? 0} icon={Database} color="bg-indigo-600" />
          <StatCard title="ISO Units" count={status?.catalogs?.iso_unit_count ?? 0} icon={Shapes} color="bg-emerald-600" />
          <StatCard title="Pending Triage" count={status?.suggestions?.pending ?? 0} icon={AlertTriangle} color="bg-amber-600" />
          <StatCard title="Categories" count={status?.categories?.total ?? 0} icon={BrainCircuit} color="bg-purple-600" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <QuickAction label="Open Mappings" icon={Database} color="bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => navigate('/interpreter/mappings')} />
        <QuickAction label="Open Triage" icon={AlertTriangle} color="bg-amber-100 text-amber-800 hover:bg-amber-200" onClick={() => navigate('/interpreter/triage')} />
        <QuickAction label="Open Schemas" icon={BrainCircuit} color="bg-purple-100 text-purple-800 hover:bg-purple-200" onClick={() => navigate('/interpreter/schemas')} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Waves className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-gray-900">Runtime Summary</h3>
          </div>
          {statusQ.isLoading ? (
            <div className="h-28 animate-pulse bg-gray-50 rounded-lg" />
          ) : (
            <div className="space-y-3 text-sm text-gray-700">
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Catalog source</div>
                <div className="font-medium">{status?.interpreter_runtime?.source || 'Unavailable'}</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Operational note</div>
                <div>
                  Interpreter mappings now live in PIM DB and are refreshed into the runtime cache. Use the Mappings tab for catalog edits and Triage for approvals/replays.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw className="w-4 h-4 text-slate-600" />
            <h3 className="text-sm font-semibold text-gray-900">Catalog Health</h3>
          </div>
          <div className="space-y-3 text-xs text-gray-600">
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="font-medium text-gray-900">UNSPSC updated</div>
              <div className="mt-1">{status?.catalogs?.unspsc_updated_at || 'Unknown'}</div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="font-medium text-gray-900">ISO updated</div>
              <div className="mt-1">{status?.catalogs?.iso_updated_at || 'Unknown'}</div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="font-medium text-gray-900">Suggestions total</div>
              <div className="mt-1">{status?.suggestions?.total ?? 0}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
